import { getDefaultClient } from '../../../client/defaultClient';
import { assertWalletProviderMethodDefined } from '../utils/assertWalletProviderMethodDefined';
import { getSignInMessage } from '../utils/getSignInMessage';
import { getWalletProviderFromWalletAccount } from '../utils/getWalletProviderFromWalletAccount';
import type { WalletAccount } from '../walletAccount';

type ProveWalletAccountOwnershipParams = {
  walletAccount: WalletAccount;
};

/**
 * Proves ownership of a wallet account by signing a verification message.
 *
 * This function generates a sign-in message and requires the user to sign it
 * with their wallet's private key, proving they control the wallet address.
 *
 * @param params.walletAccount - The wallet account to prove ownership of.
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves to an object containing the message and signature.
 */
export const proveWalletAccountOwnership = async (
  { walletAccount }: ProveWalletAccountOwnershipParams,
  client = getDefaultClient()
): Promise<{
  messageToSign: string;
  signature: string;
}> => {
  const walletProvider = getWalletProviderFromWalletAccount(
    {
      walletAccount,
    },
    client
  );

  assertWalletProviderMethodDefined(walletProvider, 'signMessage');

  const messageToSign = await getSignInMessage({ walletAccount }, client);

  const { signature } = await walletProvider.signMessage({
    message: messageToSign,
    walletAccount,
  });

  return { messageToSign, signature };
};
